import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import DateRangePicker from '../../components/common/DateRangePicker';
import { usePagedData } from '../../hooks/usePagedData';
import { amazonShipmentsApi } from '../../api/endpoints';
import { FulfilledShipmentList, FulfilledShipmentSummary } from '../../types';
import Select from '../../components/common/Select';
import {
  FiRefreshCw, FiSearch, FiPackage, FiTruck, FiCalendar, FiBox,
} from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { toast } from 'react-toastify';
import CountryFlag from '../../components/common/CountryFlag';

/* ------------------------------------------------------------------ */
/*  Marketplace ID → ISO country code                                  */
/* ------------------------------------------------------------------ */

const marketplaceToCountry: Record<string, string> = {
  A1PA6795UKMFR9: 'DE',
  A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1F83G8C2ARO7P: 'GB',
  A1805IZSGTT6HS: 'NL',
  A1C3SOZRARQ6R3: 'PL',
  A2NODRKZP88ZB9: 'SE',
  A33AVAJ2PDY3EV: 'TR',
  A2VIGQ35RCS4UG: 'AE',
  ARBP9OOSHTCHU: 'EG',
  A17E79C6D8DWNP: 'SA',
};

const getMarketplaceCountry = (m: string | null): string | null => {
  if (!m) return null;
  return marketplaceToCountry[m] || null;
};

/* ------------------------------------------------------------------ */
/*  Channel badge                                                      */
/* ------------------------------------------------------------------ */

const ChannelBadge: React.FC<{ channel: string | null }> = ({ channel }) => {
  if (!channel) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const isAfn = channel === 'AFN' || channel === 'Amazon';
  const cfg = isAfn
    ? { color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.12)', label: 'FBA' }
    : { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', label: 'FBM' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
      fontWeight: 600, color: cfg.color, backgroundColor: cfg.bg, whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Stat card                                                          */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, iconColor, label, value }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderRadius: '10px',
    backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: iconBg, color: iconColor, fontSize: '16px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
        {value}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const FulfilledShipmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<FulfilledShipmentSummary | null>(null);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<FulfilledShipmentList>(amazonShipmentsApi.fulfilled.list, {
    initialSortBy: 'shipmentDate',
    initialSortDesc: true,
  });

  useEffect(() => {
    let cancelled = false;
    amazonShipmentsApi.fulfilled.summary()
      .then((resp) => { if (!cancelled) setSummary(resp.data); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [data]);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        fulfillmentChannel: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleCarrierFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        carrier: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleDateFromChange = useCallback((value: string) => {
    setDateFrom(value);
    setFilters((prev: Record<string, any>) => ({ ...prev, dateFrom: value || undefined }));
  }, [setFilters]);

  const handleDateToChange = useCallback((value: string) => {
    setDateTo(value);
    setFilters((prev: Record<string, any>) => ({ ...prev, dateTo: value || undefined }));
  }, [setFilters]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await amazonShipmentsApi.fulfilled.sync();
      toast.success(t('amazonShipments.syncStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('amazonShipments.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh, t]);

  const formatCurrency = (amount: number | null, currency?: string | null) => {
    if (amount === null || amount === undefined) return '—';
    const cur = currency || 'EUR';
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: cur });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const carrierOptions = useMemo(() => {
    const carriers = Object.keys(summary?.byCarrier || {});
    return [
      { value: '', label: t('amazonShipments.allCarriers') },
      ...carriers.map(c => ({ value: c, label: c })),
    ];
  }, [summary, t]);

  const columns = useMemo(() => [
    {
      key: 'shipmentDate',
      header: t('amazonShipments.shipmentDate'),
      sortable: true,
      render: (row: FulfilledShipmentList) => (
        <span style={{ fontSize: '13px', fontWeight: 600 }}>
          {formatDate(row.shipmentDate)}
        </span>
      ),
    },
    {
      key: 'amazonOrderId',
      header: t('amazonShipments.orderId'),
      render: (row: FulfilledShipmentList) => (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}>
          {row.amazonOrderId}
        </span>
      ),
    },
    {
      key: 'product',
      header: t('amazonShipments.product'),
      render: (row: FulfilledShipmentList) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
          <span style={{
            fontSize: '13px', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '320px',
          }}>
            {row.productName || '—'}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
            {row.asin && <>ASIN: {row.asin}</>}
            {row.sku && <> · SKU: {row.sku}</>}
          </span>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: t('amazonShipments.qty'),
      render: (row: FulfilledShipmentList) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '26px', height: '22px', padding: '0 8px',
          borderRadius: '11px', fontSize: '12px', fontWeight: 600,
          backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text)',
        }}>
          {row.quantityShipped}
        </span>
      ),
    },
    {
      key: 'channel',
      header: t('amazonShipments.channel'),
      className: 'col-hide-mobile',
      render: (row: FulfilledShipmentList) => <ChannelBadge channel={row.fulfillmentChannel} />,
    },
    {
      key: 'tracking',
      header: t('amazonShipments.tracking'),
      render: (row: FulfilledShipmentList) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {row.carrier && (
            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>{row.carrier}</span>
          )}
          {row.trackingNumber ? (
            <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono', monospace", color: 'var(--text-muted)' }}>
              {row.trackingNumber}
            </span>
          ) : (
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>—</span>
          )}
        </div>
      ),
    },
    {
      key: 'destination',
      header: t('amazonShipments.destination'),
      className: 'col-hide-mobile',
      render: (row: FulfilledShipmentList) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
          <CountryFlag countryCode={getMarketplaceCountry(row.marketplace) || row.shipCountry} size={14} />
          <span style={{ color: 'var(--text-muted)' }}>
            {row.shipCity ? `${row.shipCity}, ${row.shipCountry || ''}` : (row.shipCountry || '—')}
          </span>
        </span>
      ),
    },
    {
      key: 'price',
      header: t('amazonShipments.price'),
      sortable: true,
      render: (row: FulfilledShipmentList) => (
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>
          {formatCurrency(row.itemPrice, row.currency)}
        </span>
      ),
    },
  ], [t]);

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('amazonShipments.fulfilledTitle')}</h1>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('amazonShipments.syncing') : t('amazonShipments.syncWithAmazon')}
          </button>
        </div>
      </div>

      {summary && (
        <div className="po-stats-grid">
          <StatCard
            icon={<FiPackage />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3b82f6"
            label={t('amazonShipments.statTotalShipments')}
            value={summary.totalShipments}
          />
          <StatCard
            icon={<FiBox />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('amazonShipments.statUnitsShipped')}
            value={summary.totalUnitsShipped}
          />
          <StatCard
            icon={<FiCalendar />}
            iconBg="rgba(124, 58, 237, 0.15)"
            iconColor="#7c3aed"
            label={t('amazonShipments.statLastWeek')}
            value={summary.lastWeekCount}
          />
          <StatCard
            icon={<FiTruck />}
            iconBg="rgba(245, 158, 11, 0.15)"
            iconColor="#f59e0b"
            label={t('amazonShipments.statLastMonth')}
            value={summary.lastMonthCount}
          />
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('amazonShipments.searchFulfilledPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('amazonShipments.allChannels') },
              { value: 'AFN', label: 'FBA (AFN)' },
              { value: 'MFN', label: 'FBM (MFN)' },
            ]}
            value={String(filters.fulfillmentChannel ?? '')}
            onChange={handleChannelFilter}
            placeholder={t('amazonShipments.allChannels')}
          />

          <Select
            options={carrierOptions}
            value={String(filters.carrier ?? '')}
            onChange={handleCarrierFilter}
            placeholder={t('amazonShipments.allCarriers')}
          />
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={7} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
        />
      )}
    </div>
  );
};

export default FulfilledShipmentsPage;
